/**
 * SRT product filter.
 *
 * The filter module processes product inventory JSON into finalized product objects, and generates recommendations based on selections.
 *
 * @module core/filter
 */

import * as App from './app';
import * as Util from './util';

/**
 * Default filter configuration settings. These settings are exported and included in [`App.config`]{@link module:core/app~config}. For more information on configuration options, see [`filter`]{@link module:core/config.filter} setting.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @see [`settings.filter`]{@link module:core/config.filter}
 */
export const config = {
  src_fallback: '',
  debug: false,
  product_key_as_sku: false,
  max_dynamic_filter_remove: 4,
  persistent_inventory: false,
  filter_options: {
    filter_on: {},
    quantity: -1,
    max_primary_results: 3,
    match_threshold: 85,
    sort: ['score'],
    prioritize: {},
  },
  sort_view_all: [
    'form_factor',
    'processor',
  ],
  display_product_specs: [
    'os',
    'processor',
    'storage_amount',
    'display',
  ],
  weights: {
    decay: 10,
    decay_scale: 0.5,
    upsell_scale: 1,
    linear_normalize: 1,
    log_base: 2,
  },
  schema: {
    backlit_keyboard: {
      type: 'boolean',
      weights: {
        value: 1,
      },
    },
    brand: {
      type: 'string',
      force_string: true,
      weights: {
        value: 6,
      },
    },
    discrete_graphics: {
      type: 'boolean',
      weights: {
        value: 8,
      },
    },
    discrete_graphics_model: {
      type: 'string',
      force_label: true,
      weights: {
        value: 5,
        decay: 1,
        decay_scale: 0.9,
      },
      values: [
        { value: 'rtx4090', label: 'GeForce RTX 4090', includes: ['rtx 4090'], brand: 'nvidia', group: 'a' },
        { value: 'rtx4080', label: 'GeForce RTX 4080', includes: ['rtx 4080'], brand: 'nvidia', group: 'a' },
        { value: 'rx7900', label: 'Radeon RX 7900', includes: ['rx 7900'], brand: 'amd', group: 'a' },
        { value: 'rtx4070', label: 'GeForce RTX 4070', includes: ['rtx 4070'], brand: 'nvidia', group: 'a' },
        { value: 'rx6950', label: 'Radeon RX 6950', includes: ['rx 6950'], brand: 'amd', group: 'a' },
        { value: 'rtx3090', label: 'GeForce RTX 3090', includes: ['rtx 3090'], brand: 'nvidia', group: 'a' },
        { value: 'rx6900', label: 'Radeon RX 6900', includes: ['rx 6900'], brand: 'amd', group: 'a' },
        { value: 'rtx3080', label: 'GeForce RTX 3080', includes: ['rtx 3080'], brand: 'nvidia', group: 'a' },
        { value: 'rtxa6000', label: 'RTX A6000', includes: ['rtx a6000'], brand: 'nvidia', group: 'a' },
        { value: 'rx6800', label: 'Radeon RX 6800', includes: ['rx 6800'], brand: 'amd', group: 'a' },
        { value: 'rtx4060', label: 'GeForce RTX 4060', includes: ['rtx 4060'], brand: 'nvidia', group: 'a' },
        { value: 'rtx3070', label: 'GeForce RTX 3070', includes: ['rtx 3070'], brand: 'nvidia', group: 'a' },
        { value: 'titanrtx', label: 'TITAN RTX', includes: ['titan rtx'], brand: 'nvidia', group: 'a' },
        { value: 'rx6700', label: 'Radeon RX 6700', includes: ['rx 6700', 'rx 6750'], brand: 'amd', group: 'a' },
        { value: 'rtx2080', label: 'GeForce RTX 2080', includes: ['rtx 2080'], brand: 'nvidia', group: 'a' },
        { value: 'rtx3060', label: 'GeForce RTX 3060', includes: ['rtx 3060'], brand: 'nvidia', group: 'a' },
        { value: 'rtx4050', label: 'GeForce RTX 4050', includes: ['rtx 4050'], brand: 'nvidia', group: 'a' },
        { value: 'a770', label: 'Intel® Arc™ A770', includes: ['a770'], brand: 'intel', group: 'a' },
        { value: 'rtxa4000', label: 'RTX A4000', includes: ['rtx A4000'], brand: 'nvidia', group: 'a' },
        { value: 'qrtx5000', label: 'Quadro RTX 5000', includes: ['quadro rtx 5000'], brand: 'nvidia', group: 'a' },
        { value: 'titanx', label: 'TITAN X', includes: ['titan x'], brand: 'nvidia', group: 'b' },
        { value: 'rtx2070', label: 'GeForce RTX 2070', includes: ['rtx 2070'], brand: 'nvidia', group: 'b' },
        { value: 'rx6600', label: 'Radeon RX 6600', includes: ['rx 6600', 'rx 6650'], brand: 'amd', group: 'b' },
        { value: 'gtx1080', label: 'GeForce GTX 1080', includes: ['gtx 1080'], brand: 'nvidia', group: 'b' },
        { value: 'rx5700', label: 'Radeon RX 5700', includes: ['rx 5700'], brand: 'amd', group: 'b' },
        { value: 'rvii', label: 'Radeon VII', includes: ['radeon vii'], brand: 'amd', group: 'b' },
        { value: 'a750', label: 'Intel® Arc™ A750', includes: ['a750'], brand: 'intel', group: 'b' },
        { value: 'rtx2060', label: 'GeForce RTX 2060', includes: ['rtx 2060'], brand: 'nvidia', group: 'b' },
        { value: 'qrtx4000', label: 'Quadro RTX 4000', includes: ['quadro rtx 4000'], brand: 'nvidia', group: 'a' },
        { value: 'rx5600', label: 'Radeon RX 5600', includes: ['rx 5600'], brand: 'amd', group: 'b' },
        { value: 'rxvega64', label: 'Radeon RX Vega 64', includes: ['vega 64'], brand: 'amd', group: 'b' },
        { value: 'rxvega56', label: 'Radeon RX Vega 56', includes: ['vega 56'], brand: 'amd', group: 'b' },
        { value: 'rtx3050', label: 'GeForce RTX 3050', includes: ['rtx 3050'], brand: 'nvidia', group: 'b' },
        { value: 'gtx1660', label: 'GeForce GTX 1660', includes: ['gtx 1660'], brand: 'nvidia', group: 'b' },
        { value: 'gtx1070', label: 'GeForce GTX 1070', includes: ['gtx 1070'], brand: 'nvidia', group: 'c' },
        { value: 'rx5500', label: 'Radeon RX 5500', includes: ['rx 5500'], brand: 'amd', group: 'b' },
        { value: 'rx590', label: 'Radeon RX 590', includes: ['rx 590'], brand: 'amd', group: 'c' },
        { value: 'gtx980', label: 'GeForce GTX 980', includes: ['gtx 980'], brand: 'nvidia', group: 'c' },
        { value: 'r9fury', label: 'Radeon R9 FURY', includes: ['r9 fury'], brand: 'amd', group: 'c' },
        { value: 'rx580', label: 'Radeon RX 580', includes: ['rx 580'], brand: 'amd', group: 'c' },
        { value: 'gtx1650', label: 'GeForce GTX 1650', includes: ['gtx 1650'], brand: 'nvidia', group: 'c' },
        { value: 'gtx1060', label: 'GeForce GTX 1060', includes: ['gtx 1060'], brand: 'nvidia', group: 'c' },
        { value: 'rx6500', label: 'Radeon RX 6500', includes: ['rx 6500'], brand: 'amd', group: 'c' },
      ],
    },
    discrete_graphics_model_brand: {
      type: 'string',
      weights: {
        value: 3,
      },
      values: [
        { value: 'intel' },
        { value: 'nvidia' },
        { value: 'amd' },
      ],
    },
    discrete_graphics_model_group: {
      type: 'string',
      weights: {
        value: 5,
        decay: 7,
        decay_scale: 1,
        upsell_scale: 1.5,
      },
      values: [
        { value: 'a' },
        { value: 'b' },
        { value: 'c' },
      ],
    },
    display: {
      type: 'number',
      compare: 'min',
      weights: {
        value: 4,
        decay: 4,
        decay_scale: 0.9,
        linear: true,
      },
      format_number: {
        options: {
          style: 'unit',
          unit: 'inch',
        },
      },
      units: {
        name: 'display_units',
        optionName: 'unit',
        convert: {
          centimeter: (value) => Util.round(value * 2.54, 1),
        },
      },
    },
    display_units: {
      type: 'object',
      child: 'display_units:size',
      properties: [
        'inch',
        'centimeter',
      ],
    },
    'display_units:size': {
      type: 'number',
      compare: 'min',
    },
    display_integer: {
      type: 'number',
      weights: {
        value: 4,
        decay: 4,
        decay_scale: 0.9,
        linear: true,
      },
    },
    evo: {
      type: 'boolean',
      weights: {
        value: 1,
      },
    },
    emmc: {
      type: 'number',
      compare: 'min',
    },
    form_factor: {
      type: 'string',
      required: true,
      force_string: true,
      weights: {
        value: 20,
      },
      values: [
        { value: 'notebook', includes: ['notebook', 'laptop'] },
        { value: '2in1', includes: ['2in1', '2-in-1', '2 in 1'] },
        { value: 'desktop', includes: ['desktop'] },
      ],
    },
    game_fps: {
      type: 'object',
      child: 'game_fps:quality',
      properties: [
        'csgo',
        'hitman3',
        'wow',
        'valorant',
        'lol',
        'pubg',
      ],
    },
    'game_fps:quality': {
      type: 'object',
      child: 'game_fps:quality:fps',
      properties: [
        '1080p_ultra',
        '1440p_ultra',
        '4k_medium',
        '4k_ultra',
      ],
    },
    'game_fps:quality:fps': {
      type: 'number',
      compare: 'min',
    },
    hdd: {
      type: 'number',
      compare: 'min',
    },
    leveling_segment: {
      type: 'string',
      values: [
        {value: 'd'},
        {value: 'c'},
        {value: 'b'},
        {value: 'a'},
      ],
      weights: {
        value: 9,
        decay: 6,
      },
    },
    os: {
      type: 'string',
      weights: {
        value: 9,
      },
      values: [
        { value: 'windows' },
        { value: 'chrome' },
        { value: 'linux' },
      ],
    },
    pen_enabled: {
      type: 'boolean',
      weights: {
        value: 2,
      },
    },
    ports: {
      type: 'object',
      child: 'ports:count',
      properties: [
        'thunderbolt_1',
        'thunderbolt_2',
        'thunderbolt_3',
        'thunderbolt_4',
        'usb_c',
        'usb_a',
        'hdmi',
        'micro_sd',
        'surface',
        'headphone',
        'headphone_mic',
      ],
    },
    'ports:count': {
      type: 'number',
    },
    price: {
      type: 'number',
      compare: 'max',
      weights: {
        value: 3,
        decay: 5,
        decay_scale: 0.8,
        upsell_scale: 1.2,
      },
      format_number: {
        options: {
          style: 'currency',
          currency: 'USD',
        },
      },
      units: {
        name: 'price_currencies',
        optionName: 'currency',
      },
    },
    price_currencies: {
      type: 'object',
      child: 'price_currencies:amount',
      properties: [
        'BRL',
        'CAD',
        'CNY',
        'CZK',
        'DKK',
        'EUR',
        'GBP',
        'ILS',
        'JPY',
        'MXN',
        'PLN',
        'RUB',
        'SAR',
        'SEK',
        'TRY',
        'TWD',
        'USD',
      ],
    },
    'price_currencies:amount': {
      type: 'number',
      compare: 'max',
    },
    priority: {
      type: 'number',
      order: 'ASC',
      weights: {
        value: 2,
        decay: 1,
        decay_scale: 1,
        linear: true,
      },
    },
    processor: {
      type: 'string',
      force_label: true,
      weights: {
        value: 8,
        decay: 2,
        decay_scale: 0.7,
        upsell_scale: 1.3,
      },
      values: [
        { value: 'xeone', brand: 'intel', includes: ['xeon e'] },
        { value: 'xeonw', brand: 'intel', includes: ['xeon w'] },
        { value: 'xeond', brand: 'intel', includes: ['xeon d'] },
        { value: 'core-ultra-9', brand: 'intel', includes: ['ultra 9'] },
        { value: 'i9', brand: 'intel' },
        { value: 'core-ultra-7', brand: 'intel', includes: ['ultra 7'] },
        { value: 'i7', brand: 'intel' },
        { value: 'core-7', brand: 'intel', includes: ['core 7'] },
        { value: 'r9', brand: 'amd', includes: ['ryzen 9', 'ryzen9'] },
        { value: 'core-ultra-5', brand: 'intel', includes: ['ultra 5'] },
        { value: 'i5', brand: 'intel' },
        { value: 'core-5', brand: 'intel', includes: ['core 5'] },
        { value: 'r7', brand: 'amd', includes: ['ryzen 7', 'ryzen7'] },
        { value: 'i3', brand: 'intel' },
        { value: 'core-3', brand: 'intel', includes: ['core 3'] },
        { value: 'r5', brand: 'amd', includes: ['ryzen 5', 'ryzen5'] },
        { value: 'r3', brand: 'amd', includes: ['ryzen 3', 'ryzen3'] },
        { value: 'processor', brand: 'intel', includes: ['n200'] },
        { value: 'pentium', brand: 'intel' },
        { value: 'a12', brand: 'amd' },
        { value: 'celeron', brand: 'intel' },
        { value: '3020e', brand: 'amd' },
        { value: 'atom', brand: 'intel' },
        { value: 'athlon-gold', brand: 'amd', includes: ['athlon gold'] },
        { value: 'athlon-silver', brand: 'amd', includes: ['athlon silver'] },
        { value: 'a10', brand: 'amd' },
        { value: 'a9', brand: 'amd' },
        { value: 'm3', brand: 'intel' },
        { value: 'a6', brand: 'amd' },
        { value: 'a4', brand: 'amd' },
        { value: 'mtek', brand: 'mediatek', includes: ['media tek', 'mediatek'] },
        { value: 'qualcomm', brand: 'qualcomm' },
      ],
    },
    processor_brand: {
      type: 'string',
      weights: {
        value: 1,
      },
      values: [
        { value: 'intel' },
        { value: 'amd' },
        { value: 'mediatek' },
        { value: 'qualcomm' },
      ],
    },
    processor_gen: {
      type: 'number',
      weights: {
        value: 2,
        decay: 3,
        decay_scale: 0.8,
        upsell_scale: 1.2,
        linear: true,
      },
    },
    processor_group: {
      type: 'string',
      weights: {
        value: 5,
      },
    },
    ram: {
      type: 'number',
      compare: 'min',
      weights: {
        value: 5,
        decay: 5,
        upsell_scale: 2,
      },
    },
    ram_type: {
      type: 'string',
      force_string: true,
    },
    ratings: {
      type: 'number',
      compare: 'min',
      weights: {
        value: 2,
        decay: 3,
        decay_scale: 0.9,
        linear: true,
        linear_normalize: 0.5,
      },
    },
    resolution: {
      type: 'string',
      force_string: true,
    },
    resolution_w: {
      type: 'number',
      compare: 'min',
    },
    resolution_h: {
      type: 'number',
      compare: 'min',
    },
    resolution_name: {
      type: 'string',
      weights: {
        value: 3,
        decay: 5,
        decay_scale: 1,
        upsell_scale: 1.2,
      },
      values: [
        { value: '8k' },
        { value: '5k' },
        { value: '4k' },
        { value: '1440p' },
        { value: '1080p' },
        { value: '720p' },
      ],
    },
    score: {
      type: 'number',
    },
    ssd: {
      type: 'number',
      compare: 'min',
    },
    stock_count: {
      type: 'boolean',
    },
    stock_info: {
      type: 'boolean',
    },
    storage: {
      type: 'object',
      child: 'storage:amount',
      properties: [
        'ssd',
        'hdd',
        'emmc',
      ],
    },
    'storage:amount': {
      type: 'number',
      compare: 'min',
    },
    storage_amount: {
      type: 'number',
      compare: 'min',
      weights: {
        value: 6,
        decay: 5,
        decay_scale: 0.7,
        upsell_scale: 2,
      },
    },
    storage_type: {
      type: 'string',
      weights: {
        value: 2,
      },
      values: [
        { value: 'ssd', label: 'SSD' },
        { value: 'hdd', label: 'HDD' },
        { value: 'emmc', label: 'eMMC' },
      ],
    },
    touchscreen: {
      type: 'boolean',
      weights: {
        value: 2,
      },
    },
    weight: {
      type: 'number',
      compare: 'max',
      weights: {
        value: 2,
        decay: 3,
        decay_scale: 1,
        upsell_scale: 0.8,
        linear: true,
        linear_normalize: 0.5,
      },
      format_number: {
        options: {
          style: 'unit',
          unit: 'pound',
        },
      },
      units: {
        name: 'weight_units',
        optionName: 'unit',
        convert: {
          default: {
            kilogram: (value) => Util.round(value * 2.205, 2),
          },
          kilogram: (value) => Util.round(value * 0.453, 2),
        },
      },
    },
    weight_units: {
      type: 'object',
      child: 'weight_units:amount',
      properties: [
        'pound',
        'kilogram',
      ],
    },
    'weight_units:amount': {
      type: 'number',
      compare: 'max',
    },
  },
};

/**
 * Filter internal settings object. Holds states and settings used during processing.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @example
 * data: {
 *   // all linted products
 *   products: [],
 *   // recommendation results
 *   results: [],
 *   // current filter request
 *   request: {},
 *   // current process
 *   process: null,
 * }
 */
let data = {
  products: [],
  results: [],
  request: {},
  process: null,
};

/**
 * Initialize filter. Load product data and lint products. Resolves or rejects promise once complete.
 *
 * - If no filter `src` is provided, do nothing.
 * - If `persistent_inventory` [filter setting]{@link module:core/config.filter} is enabled:
 *     - Attempt to [get products from database]{@link module:core/app.db.getAll}. If products exist, finalize products.
 * - If not using persistent inventory, or no persistent inventory is found:
 *     - Load inventory JSON from [filter]{@link module:core/config.filter} `src`.
 *     - Create inventory array for use with filter by cloning each product object and moving product object key into product as `product_id`, then finalize products.
 * - [Lint products]{@link module:core/filter~lintProducts}.
 * - Filter out undesired products by checking each product [against include conditions]{@link module:core/filter~includeProduct}
 * - If persistent inventory, [store products in database]{@link module:core/app.db.putAll} and resolve promise. Otherwise just resolve.
 *
 * If filter `src` is provided as a blank string, the promise will resolve silently without Filter initialization. This is useful for projects or customizations that do not require inventory processing or in-app product recommendations.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @return {Promise}
 *
 * @see [`includeProduct`]{@link module:core/filter~includeProduct}
 * @see [`lintProducts`]{@link module:core/filter~lintProducts}
 * @see [`db.getAll`]{@link module:core/app.db.getAll}
 * @see [`db.putAll`]{@link module:core/app.db.putAll}
 * @see Used by:
 * @see [**`App.init`**]{@link module:core/app.init}
 */
export const init = () => {
  return new Promise((resolve, reject) => {
    // if we have no filter source, resolve without initializing
    if (!App.settings.filter.src) resolve();
    // persistent inventory settings
    const persistent = App.settings.filter.persistent_inventory;

    // load json inventory
    const getJSONInventory = (fallback = false) => {
      // if attempting fallback, update filter src
      if (fallback) App.updateSettings({
        filter: {
          src: App.settings.filter.src_fallback,
          src_failed: App.settings.filter.src,
        },
      });
      // ajax load json
      $.getJSON(App.settings.filter.src)
        // on success
        .then((inventory) => {
          debug('Loaded inventory JSON.');
          // get products ready for linting
          let products = Object.keys(inventory).map((id) => {
            let product = Util.cloneDeep(inventory[id]);
            product.product_id = id;
            return product;
          });
          // finalize products
          finalize(products);
        })
        // on failure, try fallback source, if one exists and has not yet been tried
        .catch((e) => (!fallback && App.settings.filter.src_fallback) ? getJSONInventory(true) : reject(e));
    }

    // get stored products
    const getStoredInventory = () => {
      App.db.getAll('products')
        .then(products => {
          // if products, finalize, otherwise load JSON
          products.length ? finalize(products) : getJSONInventory();
        }, () => {
          // load JSON on fail
          getJSONInventory();
        });
    };

    const finalize = (products) => {
      // set products
      data.products = products;
      // lint products
      lintProducts(data.products);
      // filter products based on include conditions
      // pre-filter count
      let preFilterCount = data.products.length;
      data.products = data.products.filter((product) => includeProduct(product));
      // if no products, and a fallback source exists which has not already been attempted, start over with it instead
      let fallback = App.settings.filter.src_fallback && App.settings.filter.src !== App.settings.filter.src_fallback;
      if (!data.products.length && fallback) {
        getJSONInventory(true);
        return;
      }
      // report product stats to buildInfo
      addProductBuildInfo(preFilterCount);
      // if persistent, store products and resolve, otherwise just resolve
      persistent ? App.db.putAll(data.products, 'products').then(() => { resolve() }).catch(e => reject(e)) : resolve();
      // report products
      debug('%cAll products', 'font-weight:bold; color:cyan;', data.products);
    };

    // get either stored or json inventory
    persistent ? getStoredInventory() : getJSONInventory();
  });
}

/**
 * Find recommended products based on provided filter request. Process overview:
 *
 * - [Set current request filter request]{@link module:core/filter~setFilterRequest} based on provided filter request, and initialize some working settings required for the recommend process.
 * - Get array of exact matches to use for sorting and prioritizing recommendation. Note that if `matches_only` is set in filter request, only these products will be returned, bypassing weighting and finalization.
 * - Get array of recommendation results using [weighting]{@link module:core/filter~weightedResults} and [finalization]{@link module:core/filter~finalizeResults}.
 *
 * Final recommendation results format will vary based on provided arguments:
 * - If the `ids` param is enabled, products will be return as an array of product IDs, instead of full products.
 * - If the `meta` param is enabled, an object will be returned which contains the array product objects or IDs as `products` property, and the results metadata object as `productMeta` property. Product metadata includes the following properties:
 *     - `requestQuantity` - filter request quantity.
 *     - `primaryCount` - number of exact matches for a given filter request.
 *     - `paddedCount` - number of additional products beyond the primary results, up to the request limit.
 *     - `nonIntelCount` - number of non-intel products found in results. Non-Intel products in the main recommendation result in excess of the non-Intel limit of 1 product will be removed.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} filterRequest - Filter request object, containing filter options and filterable attributes.
 * @param {boolean} [ids] - Return as product IDs or full products. Default: `false`
 * @param {boolean} [meta] - When enabled, includes product meta along with product results. Returns an object instead of an array. Default: `false`
 *
 * @return {array|object}
 *
 * @see [`setFilterRequest`]{@link module:core/filter~setFilterRequest}
 * @see [`filterProducts`]{@link module:core/filter~filterProducts}
 * @see [`weightedResults`]{@link module:core/filter~weightedResults}
 * @see [`finalizeResults`]{@link module:core/filter~finalizeResults}
 * @see [`resetProcess`]{@link module:core/filter~resetProcess}
 *
 * @example
 * // example request
 * let request = {
 *   quantity: 3,
 *   filter_on: {
 *     form_factor: "notebook",
 *     processor: "i7",
 *   }
 * }
 *
 * @example
 * // get full products
 * recommend(request);
 *
 * // example result
 * [
 *   {
 *     product_id: '8110568',
 *     name: 'Product 1',
 *     ...
 *   },
 *   {
 *     product_id: '8213745',
 *     name: 'Product 2',
 *     ...
 *   },
 *   {
 *     product_id: '9002545',
 *     name: 'Product 3',
 *     ...
 *   },
 * ]
 *
 * @example
 * // get product ids
 * recommend(request, true);
 *
 * // example result
 * ['8110568', '8213745', '9002545']
 *
 * @example
 * // get product ids with product meta
 * recommend(request, true, true);
 *
 * // example result
 * {
 *   products: ['8110568', '8213745', '9002545'],
 *   productMeta: {
 *     requestQuantity: 3,
 *     primaryCount: 2,
 *     paddedCount: 1,
 *     nonIntelCount: 1,
 *   },
 * }
 *
 */
export const recommend = (filterRequest, ids = false, meta = false) => {
  // set current process
  data.process = 'recommend';
  // set up filter request
  setFilterRequest(filterRequest);
  // setup some process settings
  // make sure results are empty
  data.results = [];
  // reset results metadata
  data.resultsMeta = {
    requestQuantity: data.request.quantity,
    paddedCount: 0,
    primaryCount: 0,
    nonIntelCount: 0,
  };
  // report original filter request
  debug('%cFilter request', 'font-weight:bold; color:white;', data.request);
  // get exact matches
  let matches = filterProducts();
  // if only exact matches are requested, add matches to results
  if (data.request.matches_only) data.results = matches;
  // otherwise continue with weighting and finalization process
  else data.results = finalizeResults(weightedResults(), matches);
  // determine if only product IDs are needed, reduce if necessary
  let products = ids ? data.results.map(product => product.product_id) : data.results;
  // reset process to default state
  resetProcess();
  // if product meta is requested, return meta object. otherwise return products array
  return meta ? { products, productMeta: data.resultsMeta } : products;
}

/**
 * Run faceted filtering on form field elements within a view.
 *
 * When [called in a view]{@link module:core/view.facet.filter} with faceting-enabled [form fields]{@link module:core/config._field_type}, this method will process current selections from the view, filter products based on those selections, and return an object that contains the remaining available options. Remaining options, in this case, represent options that can be selected and still yield product results.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} filterRequest - Filter request object, containing filter options and filterable attributes.
 * @param {object} facetObject - All filter choices that we should run faceting on.
 *
 * @return {object}
 *
 * @see [`setFilterRequest`]{@link module:core/filter~setFilterRequest}
 * @see [`filterProducts`]{@link module:core/filter~filterProducts}
 * @see [`availableProductAttr`]{@link module:core/filter.availableProductAttr}
 * @see [`App.attributeMatch`]{@link module:core/app.attributeMatch}
 * @see [`resetProcess`]{@link module:core/filter~resetProcess}
 * @see Used by:
 * @see [`View.facet.filter`]{@link module:core/view.facet.filter}
 *
 * @example
 * // For this example, assume:
 * // 1. we have a screen with two selection inputs: form factor and processor;
 * // 2. user has selected the "notebook" form factor; and
 * // 3. there are no notebooks with i7 processors in our product data.
 *
 * // All possible selections that a user could make are gathered into facetObject
 * facetObject = {
 *   form_factor: ["notebook", "desktop"]
 *   processor: ["i3", "i5", "i7"]
 * }
 *
 * // Currently selected options are gathered in filterRequest.filter_on
 * filterRequest: {
 *   filter_on: {
 *     form_factor: "notebook",
 *   },
 * }
 *
 * import * as Filter from '../core/filter';
 *
 * let remainingFilters = Filter.facet(options, facetObject);
 *
 * // expected result:
 * {
 *   form_factor: ["notebook"]
 *   processor: ["i3", "i5"]
 * }
 *
 * // since the user has chosen notebooks, the "desktops" option in the processor input should be unavailable, as well as the the "i7" option, since there are no notebooks with that option in our product data.
 */
export const facet = (filterRequest, facetObject) => {

  // set current process
  data.process = 'facet';

  setFilterRequest(filterRequest);

  if (data.request) debug('%cFacet filter request', 'font-weight:bold; color:white;', data.request);

  let products = filterProducts();

  // if no products found, return empty object
  if (!products.length) return {};

  debug('%cFacet filtered products', 'font-weight:bold; color:green;', products);
  // This is the facetObject (available filters).
  debug('%cPossible filter values (facet object)', 'font-weight:bold; color:white;', facetObject);

  // determine available attribute values
  let productAttr = availableProductAttr({ products, facetObject, attrPathObj: data.request.filter_on });

  debug('%cAvailable product attributes', 'font-weight:bold; color:white;', productAttr);

  let availableAttr = {};
  // intersect facetObject and available properties to find remaining filters
  for (let [attribute, array] of Object.entries(facetObject)) {
    // skip attribute if not available
    if (!productAttr[attribute]) continue;
    // find intersecting elements between facetObject property values and available values
    let available = array.filter((request) => {
      // reduce available properties to boolean showing if any remaining product attributes match
      return productAttr[attribute].reduce((m, v) => App.attributeMatch(attribute, request, v) ? true : m, false);
    });
    // add to found filters
    if (available.length) availableAttr[attribute] = available;
  }

  debug('%cAvailable filter values', 'font-weight:bold; color:white;', availableAttr);

  // reset process
  resetProcess();

  // return found filters
  return availableAttr;
}

/**
 * Find available attribute values for a group of products. Returns an attribute object, where each attribute property contains an array of found values.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {object} args
 *
 * @param {array} [args.products] - Array of products for which to find attribute values. Default: `data.products` (all available products)
 *
 * @param {array} [args.attributes] - Array of attribute names to look for. Default: `[filter]{@link module:core/config.filter}.schema` keys
 *
 * @param {object} [args.attrPathObj] - If complex attributes are present in the attributes array, an attribute object which contains related attributes must be present in order to determine valid paths to the product attributes. Default: `{}`
 *
 * @param {object} [args.facetObject] - If complex attributes are present in the attributes array, any object-type attributes must have at least one child value available in order to be counted as available. Child values must be provided through an attribute object which contains possible values for each attribute.
 *
 * @param {boolean} [args.sort] - When enabled, values will be sorted based on their value. Disable this setting if products are already in desired order. Default: `true`
 *
 * @return {object}
 *
 * @see [`App.attributeMatch`]{@link module:core/app.attributeMatch}
 * @see Used by:
 * @see [`facet`]{@link module:core/filter.facet}
 *
 * @example
 * // provided products
 * let products = [
 *   {
 *     name: 'Product 1',
 *     attributes: {
 *       processor: 'i7',
 *       ram: 16
 *     }
 *   },
 *   {
 *     name: 'Product 2',
 *     attributes: {
 *       processor: 'i5',
 *       ram: 8
 *     }
 *   },
 *   {
 *     name: 'Product 3',
 *     attributes: {
 *       processor: 'i3',
 *       ram: 8
 *     }
 *   },
 * ]
 *
 * availableProductAttr({products});
 *
 * // expected result
 * {
 *   processor: ['i3', 'i5', 'i7'],
 *   ram: [8, 16],
 * }
 */
export const availableProductAttr = ({products = data.products, attributes = Object.keys(App.settings.filter.schema), attrPathObj = {}, facetObject, sort = true} = {}) => {
  // hold found values
  let available = {};
  // determine what filter values we have remaining in filtered products
  // loop through each filter property
  for (let attribute of attributes) {
    // create value array
    let attrValues = [];
    // if product has this attribute, add it to value array
    for (let product of products) {
      // get all valid attribute paths
      let attributePaths = Util.getAttributePaths(attribute, attrPathObj);
      // if no valid attribute paths, skip
      if (!attributePaths.length) continue;
      // check if product contains any of the requested attributes
      for (let path of attributePaths) {
        // get path value
        let value = Util.getObjectValue(product.attributes, path, '.');
        // if no value, continue
        if (!value) continue;
        // create values array
        let values = [value];
        // if value is an object, add object keys
        if (typeof value === 'object' && !Array.isArray(value)) {
          // add values as object keys
          values = Object.keys(value);
          // if facet object is provided, filter keys to only those with at least one child value available
          if (facetObject) {
            // get child def name
            let childAttribute = Util.getSchemaValue('child', attribute);
            // if facet object does not contain child, skip
            if (!facetObject[childAttribute]) continue;
            // filter values
            values.filter((key) => App.attributeMatch(childAttribute, facetObject[childAttribute], Util.getObjectValue(product.attributes, `${path}.${key}`, '.'), App.settings.primary_attribute));
          }
        }
        // add value(s) to array
        Util.arrayPushUnique(attrValues, ...values);
      }
    }
    // if we found values for this attribute, add to available
    if (attrValues.length > 0) {
      // sort products, if enabled
      if (sort) attrValues.sort();
      available[attribute] = attrValues.slice();
    }
  }

  return available;
};

/**
 * Get weighted values for a given attribute, relative to a provided value for that attribute, for each available product. Only unique product values are included, and results are sorted according to their default or defined `order` in the attribute's schema definition.
 *
 * Returns an array of objects, each of which contains the properties:
 *
 * - `label` - Attribute label, or if none exists, the raw attribute value.
 * - `value` - The raw attribute value.
 * - score_detail - The product's attribute score data. See [`weightedResults`]{@link module:core/filter~weightedResults} for more info.
 *
 * @param {string} attribute - Attribute name.
 *
 * @param {*} value - Request value to compare product values against. If no value is provided, available attribute values will still be returned, but score details will not be available. Default: `false`
 *
 * @returns {array}
 *
 * @see [`recommend`]{@link module:core/filter.recommend}
 * @see Used by:
 * @see [`facet`]{@link module:core/filter.facet}
 */
export const getWeightedProductAttributes = (attribute, value = false) => {
  let prevValue;
  let filter_on = value ? Util.formatAttributes({[attribute]: value}) : {};
  return recommend({filter_on, sort: [attribute], prevent_exact_matches: true}).filter(product => {
    let value = product.attributes[attribute];
    // if attribute doesn't exist, remove product
    if (typeof value === 'undefined') return false;
    // if value is greater than 100, round to nearest hundred
    if (typeof value === 'number' && value > 100) value = Util.round(value, -2);
    // otherwise determine if value is unique
    let isUnique = prevValue !== value;
    prevValue = value;
    return isUnique;
  }).map(product => {
    return {
      label: product.labels[attribute] || product.attributes[attribute],
      value: product.attributes[attribute],
      score_detail: product.attributes.score_detail[attribute],
    }
  });
};

/**
 * Find the lowest price value in an array of products.
 *
 * Returns price as a number, or `false` if no prices are found.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array} products - Array of product objects.
 *
 * @return {number|boolean}
 *
 * @see Used by:
 * @see [**`prioritizeLowestPrice`**]{@link module:core/filter~prioritizeLowestPrice}
 * @see [**`View.facet.setLowestPrice`**]{@link module:core/view.facet.setLowestPrice}
 *
 * @example
 * let products = [
 *   {
 *     name: "Product 1",
 *     attributes: {
 *       price: 199.99,
 *     },
 *   },
 *   {
 *     name: "Product 2",
 *     attributes: {
 *       price: 249,
 *     },
 *   },
 *   {
 *     name: "Product 3",
 *     attributes: {
 *       price: 1595.49,
 *     },
 *   },
 * ]
 *
 * lowestPrice(products);
 *
 * // expected result: 199.99
 *
 * @example
 * let products = [
 *   {
 *     name: "Product 1",
 *     attributes: {
 *       processor: 'i5',
 *     },
 *   },
 *   {
 *     name: "Product 2",
 *     attributes: {
 *       processor: 'i7',
 *     },
 *   },
 * ]
 *
 * lowestPrice(products);
 *
 * // expected result: false
 */
export const lowestPrice = (products) => {
  let prices = products.map(product => product.attributes.price).filter(price => !!price);
  return prices.length ? Math.min(...prices) : false;
};

/**
 * Retrieves products by IDs. Returns an array of products.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {...string} ids - IDs of products to retrieve from linted products.
 *
 * @return {array}
 *
 * @see [**`App.setCurrentContent`**]{@link module:core/app~setCurrentContent}
 *
 * @example
 * let id = 'product_05';
 * let more_ids = ['product_13', 'product_34'];
 *
 * getProductsByID(id, ...more_ids);
 *
 * // expected result (product data is truncated for the example):
 * [
 *   {
 *     name: 'Name of product 5',
 *     product_id: 'product_05',
 *     ...
 *   },
 *   {
 *     name: 'Name of product 13',
 *     product_id: 'product_13',
 *     ...
 *   },
 *   {
 *     name: 'Name of product 34',
 *     product_id: 'product_13',
 *     ...
 *   }
 * ]
 */
export const getProductsByID = (...ids) => {
  // create products array
  let products = [];
  // loop through product ID values
  for (let id of ids) {
    // find product with matching id
    let index = Util.indexOfObjectByProperty('product_id', id, data.products);
    // if found, add to products array
    if (index !== -1) products.push(data.products[index]);
  }
  return products;
};

/**
 * Update an existing product, or add a new product.
 *
 * Product is [linted]{@link module:core/filter~lintProducts} after updates to ensure attributes and labels are up-to-date.
 *
 * If the `persistent_inventory` [filter setting]{@link module:core/config.filter} is enabled, updated product will be [stored in the database]{@link module:core/app.db.put}. If the product ID has changed, the product with the old ID is [removed from the database]{@link module:core/app.db.remove}.
 *
 * Returns the updated product.
 *
 * @method
 *
 * @since 3.2.3
 *
 * @param {string} id - ID of the product to update. If no product with this ID exists, a new one will be created using the provided data.
 * @param {object} productData - Data to add to product.
 * @param {boolean} [merge] - When enabled, provided data will be [merged]{@link module:core/util.merge} into `state` object. By default, `Object.assign` will be used to overwrite existing properties. Default: `false`
 *
 * @return {object}
 *
 * @see [`lintProducts`]{@link module:core/filter~lintProducts}
 * @see [`App.db.put`]{@link module:core/app.db.put}
 * @see [`App.db.remove`]{@link module:core/app.db.remove}
 *
 * @example
 * // existing product
 * {
 *   name: 'Dell SuperComp 18',
 *   product_id: 'product_07',
 *   exclude: false,
 * }
 *
 * updateProduct('product_07', {
 *   name: 'Dell SuperComp 2000',
 *   exclude: true,
 * });
 *
 * // updated product
 * {
 *   name: 'Dell SuperComp 2000',
 *   product_id: 'product_07',
 *   exclude: true,
 * }
 */
export const updateProduct = (id, productData, merge = false) => {
  // get product index
  let productIndex = Util.indexOfObjectByProperty('product_id', id, data.products);
  // if no product with that id exists, push a new object and define the product index
  if (productIndex === -1) {
    productIndex = data.products.length;
    data.products.push({});
  }
  // set combine function
  const combine = merge ? Util.merge : Object.assign;
  // combine product and new data
  data.products[productIndex] = combine(data.products[productIndex], productData);
  // re-lint product
  lintProducts([data.products[productIndex]]);
  // if persistent inventory is enabled
  if (App.settings.filter.persistent_inventory) {
    // id of current product object
    let storeID = data.products[productIndex].product_id;
    // update product in database
    App.db.put(data.products[productIndex], storeID, 'products').catch(e => Util.logError(e, 'Unable to store product.'));
    // if updating product id, remove old product from database
    if (storeID !== id) App.db.remove(id, 'products').catch(e => Util.logError(e, 'Unable to remove product.'));
  }
  // return updated product
  return data.products[productIndex];
};

/**
 * Delete product from the `[data]{@link module:core/filter~data}.products` filter-able products array. If the `persistent_inventory` [filter setting]{@link module:core/config.filter} is enabled, the product is also [removed from the database]{@link module:core/app.db.remove}.
 *
 * Returns the deleted product object if found, otherwise returns `false`.
 *
 * @method
 *
 * @since 3.2.3
 *
 * @param {string} id - ID of the product to delete.
 *
 * @see [`App.db.remove`]{@link module:core/app.db.remove}
 *
 * @return {array|boolean}
 */
export const deleteProduct = (id) => {
  // get product index
  let productIndex = Util.indexOfObjectByProperty('product_id', id, data.products);
  // if no product with that id exists, do nothing
  if (productIndex === -1) return false;
  // if persistent inventory update product in database
  if (App.settings.filter.persistent_inventory) App.db.remove(id, 'products').catch(e => Util.logError(e, 'Unable to delete product from database.'));
  // splice out product, return result
  return data.products.splice(productIndex, 1);
};

/**
 * Reset `[filter]{@link module:core/filter~data}.process` back to `null`.
 *
 * @since 3.0.0
 *
 * @see Used by:
 * @see [**`Filter.recommend`**]{@link module:core/filter.recommend}
 * @see [**`Filter.facet`**]{@link module:core/filter.facet}
 */
const resetProcess = () => {
  data.process = null;
};

/**
 * Prepare filter request for use in filtering.
 *
 * Sets `[data]{@link module:core/filter~data}.request` at the start of any filter request. Combines default `filter_options` [filter settings]{@link module:core/config.filter} with app-supplied filter request.
 *
 * Each attribute provided in `[data]{@link module:core/filter~data}.request.filter_on` is also checked for issues:
 *     - If there are no valid [attribute paths]{@link module:core/util.getAttributePaths} for the given attribute, it is removed from the request.
 *
 * During matrix runs:
 * - Each filter request is added to `matrix.last_filter_request` setting for use in matrix/blueprint data.
 *
 * @since 3.0.0
 *
 * @param {object} options - Object containing filter values and/or settings
 *
 * @see Used by:
 * @see [**`Filter.recommend`**]{@link module:core/filter.recommend}
 * @see [**`Filter.facet`**]{@link module:core/filter.facet}
 *
 * @example
 * // app filter request options
 * let options = {
 *   quantity: 3,
 *   lowest_price: true,
 *   filter_on: {
 *     form_factor: "notebook",
 *     processor: "i7",
 *     storage_amount: "500",
 *   }
 * }
 *
 * // config.filter_options defaults
 * {
 *   shuffle: true,
 *   priority: false,
 *   lowest_price: false,
 * }
 *
 * // run method
 * setFilterRequest(options);
 *
 * // expected filter.options value
 * {
 *   quantity: 3,
 *   shuffle: true,
 *   priority: false,
 *   lowest_price: true,
 *   filter_on: {
 *     form_factor: "notebook",
 *     processor: "i7",
 *     storage_amount: 500,
 *   }
 * }
 */
const setFilterRequest = (options) => {
  // set filter request
  data.request = Object.assign({}, App.settings.filter.filter_options, options)
  // validate request attributes
  let attributesValid = true;
  // check each attribute
  for (let attribute of Object.keys(data.request.filter_on)) {
    // if no valid attribute paths, remove attribute
    if (!Util.getAttributePaths(attribute, data.request.filter_on).length) {
      delete data.request.filter_on[attribute];
      debug('removed invalid attribute:', attribute);
    }
  }
  // throw error if any attributes are invalid
  if (!attributesValid) throw "Error validating attributes.";
  // do matrix things
  if (App.settings.env === 'matrix') {
    // store filter request in settings, without filter_on values
    let filter_options = Util.cloneDeep(data.request);
    delete filter_options.filter_on;
    App.updateSettings({ matrix: { last_filter_request: filter_options }});
  }
};

/**
 * Prepare an array of products for filter usage.
 *
 * During the linting process, each provided product is passed through a series of functions to process and prepare its data (product is updated in place). An overview of this process:
 *
 * 1. If the config setting `product_key_as_sku` is enabled, the `product_id` value will be copied to the `sku` property.
 * 1. [Image URL will be updated]{@link module:core/filter~setImageURL}, if the Intel image API service is to be used.
 * 1. [Product attributes are set]{@link module:core/filter~setProductAttributes} as the `attributes` object, which act as the "filterable" properties of a product. These are the values that will be tested to see if a product is a match for a given filter request.
 * 1. [Product labels are formatted]{@link module:core/filter~setProductLabels} with any modifications necessary for final display, and added under the `labels` object. This includes price localization and generating labels for storage amounts, among other things.
 * 1. [Specs array is created]{@link module:core/filter~setProductSpecs} for any product specs that need be displayed in a products view template (e.g. `processor`, `os`, defined by `display_product_specs` [filter setting]{@link module:core/config.filter}) will be added to `specs` array as an object with `name` and `value` properties to allow looping through key/value pairs in a predictable (and ordered) way.
 *
 * @since 3.0.0
 *
 * @param {object} products - Local or API endpoint products data
 *
 * @return {array}
 *
 * @see [`setImageURL`]{@link module:core/filter~setImageURL}
 * @see [`setProductAttributes`]{@link module:core/filter~setProductAttributes}
 * @see [`includeProduct`]{@link module:core/filter~includeProduct}
 * @see [`setProductLabels`]{@link module:core/filter~setProductLabels}
 * @see [`setProductSpecs`]{@link module:core/filter~setProductSpecs}
 * @see [`App.customize`]{@link module:core/app.customize} `product`
 * @see Used by:
 * @see [**`init`**]{@link module:core/filter.init}
 * @see [**`updateProduct`**]{@link module:core/filter.updateProduct}
 *
 * @example
 * // un-linted product object
 * // note that properties may be different depending on the project
 * {
 *   name: "Dell XPS 17 9700",
 *   image: "assets/images/devices/dell-xps-17-9700.jpg",
 *   product_id: "example_product",
 *   link: "https://example.com/link-to-product",
 *   os: "Windows 10",
 *   price: 3299.99,
 *   brand: "Dell",
 *   processor: "Intel® Core™ i9 processor",
 *   hdd: "1000",
 *   ssd: "500",
 *   ram: "32",
 *   display: "17 inches",
 *   resolution: "3840 x 2160",
 *   form_factor: "Notebook"
 * }
 *
 * @example
 * // linted product object
 * {
 *   product_id: "example_product",
 *   name: "Dell XPS 17 9700",
 *   image: "assets/images/devices/dell-xps-17-9700.jpg",
 *   link: "https://example.com/link-to-product",
 *   sku: "example_product",
 *   os: "Windows 10",
 *   price: 3299.99,
 *   brand: "Dell",
 *   processor: "Intel® Core™ i9 processor",
 *   hdd: "1000",
 *   ssd: "500",
 *   ram: "32",
 *   display: "17 inches",
 *   resolution: "3840 x 2160",
 *   form_factor: "Notebook"
 *   attributes: {
 *     brand: "dell",
 *     display: 17,
 *     form_factor: "notebook",
 *     os: "windows",
 *     price: 3299.99,
 *     processor: "i9",
 *     hdd: 1000,
 *     ssd: 500,
 *     ram: 32,
 *     resolution_w: 3840,
 *     resolution_h: 2160,
 *     resolution_name: "4k",
 *     storage_amount: 1500,
 *     storage_type: ["hdd", "ssd"],
 *   },
 *   labels: {
 *     brand: "Dell",
 *     os: "Windows 10",
 *     price: "3.299,99 €",
 *     processor: "Intel® Core™ i9 processor",
 *     hdd: "1000",
 *     ssd: "500",
 *     storage_amount: "1TB HDD + 500GB SSD",
 *     ram: "32 GB",
 *     display: "17\"",
 *     resolution: "3840 x 2160",
 *     form_factor: "Notebook"
 *   },
 *   specs: [
 *     {
 *       name: "os",
 *       value: "Windows 10",
 *     },
 *     {
 *       name: "processor",
 *       value: "Intel® Core™ i9 processor",
 *     },
 *     {
 *       name: "storage_amount",
 *       value: "1TB HDD + 500GB SSD",
 *     },
 *   ],
 * }
 */
const lintProducts = (products) => {
  // process products objects and add result to lintedProducts
  for (let product of products) {
    // Add product ID as SKU, if enabled
    if (App.settings.filter.product_key_as_sku) product.sku = product['product_id'];

    // Use Intel's image service, if enabled
    setImageURL(product);

    // set product filter attributes.
    // holds normalized values to match filter selections against
    setProductAttributes(product);

    // label properties, if needed
    // Processes the product labels and reformat them if necessary
    setProductLabels(product);

    // add specs to product
    // Copies any relevant properties from the main product body into specs, for display
    setProductSpecs(product);

    // customize product linting
    App.customize('lintProducts', product);
  }
};

/**
 * Add product data to `buildInfo` array, which is displayed on the view all template. The following information is added:
 *
 * - Product count, as well as excluded product count, if available
 * - Any attribute values that were unrecognized during the linting process. These attribute values may need to be added to the filter schema definition for that attribute.
 *
 * @since 3.3.1
 *
 * @param {number} preFilterCount - Number of products in inventory before filtering.
 */
const addProductBuildInfo = (preFilterCount) => {
  let invMsg = `<a href="${App.settings.filter.src}">${App.settings.filter.src}</a>`;
  if (App.settings.filter.src_failed) {
    // append message
    invMsg += ` (️️️️️️⚠️ <a href="${App.settings.filter.src_failed}">primary source</a> failed)`;
    // set build alert
    App.flagBuildIssue();
  }
  // add final filter src
  App.settings.buildInfo.push({
    name: `inventory`,
    label: invMsg,
  });

  // create product count label
  let countLabel = `${data.products.length}`;
  if (preFilterCount !== data.products.length) countLabel += ` included out of ${preFilterCount} total`;
  // hold form factor and unrecognized attributes
  let formFactors = {};
  let ur = {};
  // loop through products
  for (let product of data.products) {
    // count form factors
    let type = product.attributes.form_factor;
    formFactors[type] = formFactors[type] ? formFactors[type] + 1 : 1;
    // if no unrecognized attributes, skip
    if (!product.unrecognized) continue;
    // gather all unrecognized attributes
    for (let [name, value] of Object.entries(product.unrecognized)) {
      // add value to attribute array
      ur[name] ? Util.arrayPushUnique(ur[name], value) : ur[name] = [value];
    }
  }
  // add product count object to build info
  App.settings.buildInfo.push({
    name: `products (${countLabel})`,
    label: Object.entries(formFactors).map(([type, count]) => `${type}: ${count}`),
  });

  // report any unrecognized attributes
  if (Object.keys(ur).length) {
    let urObj = {
      name: '️️️️️️⚠️ unrecognized product attributes',
      label: [],
    };
    for (let [attribute, values] of Object.entries(ur)) urObj.label.push(`<code>${attribute}</code>: ${values.join(', ')}`);
    // add object to build info
    App.settings.buildInfo.push(urObj);
    // set build alert
    App.flagBuildIssue();
  }
};

/**
 * Determine if product should be included in inventory. Uses conditions from `include_products` [filter setting]{@link module:core/config.filter} to determine if a given product from inventory JSON should be included in filter recommendations.
 *
 * Conditions are tested using [`App.conditionalOn`]{@link module:core/app.conditionalOn}.
 *
 * @since 3.1.0
 *
 * @param {object} product - Product object.
 *
 * @return {boolean}
 *
 * @see [`App.conditionalOn`]{@link module:core/app.conditionalOn}
 * @see Used by:
 * @see [**`lintProducts`**]{@link module:core/filter~lintProducts}
 */
const includeProduct = (product) => {
  let conditions = App.settings.filter.include_products;
  // if no product conditions are specified, always include product
  if (!conditions || !conditions.length) return true;
  // determine if conditions are met
  return App.conditionalOn(conditions, false, product.attributes);
};

/**
 * Modify a product image URL to use Intel image API service, if enabled.
 *
 * If the `api_images.fallback_only` [setting]{@link module:core/config.settings} is not enabled, run the product image through [`Util.getImageAPIURL`]{@link module:core/util.getImageAPIURL}
 *
 * *Note: image URL is updated in place (no value is returned).*
 *
 * @since 3.0.0
 *
 * @param {object} product - Product object
 *
 * @see Used by:
 * @see [**`lintProducts`**]{@link module:core/filter~lintProducts}
 *
 * @example
 * // config with api images enabled
 * api_images: {
 *   enabled: true,
 *   fallback_only: false,
 *   dimensions: [480, 320],
 * }
 *
 * // original product
 * let product = {
 *   name: "Dell XPS 17 9700",
 *   image: "https://example.com/link-to-product-image.jpg"
 * }
 *
 * // run method
 * setImageURL(product);
 *
 * // updated product
 * {
 *   name: "Dell XPS 17 9700",
 *   image: "https://pim-media.intel.com/pub-api/v1/imageservice/customize?url=https://example.com/link-to-product-image.jpg&width=480&height=320"
 * }
 */
const setImageURL = (product) => {
  if (App.settings.api_images.fallback_only) return;
  product.image = Util.getImageAPIURL(product.image);
};

/**
 * Interpret product data and format them into standardized and filterable attribute values.
 *
 * Standardized attributes are stored in an object and added the product as `products.attributes`. Any attributes defined in filter [schema]{@link module:core/config.schema} are formatted automatically using their specified `type`, if found in the product.
 *
 * There are also some dynamically-generated attributes that are compiled from other product attributes, or added if missing:
 *
 * #### `processor_gen` (number)
 *
 * Attempts to determine the device's processor generation from the `processor_family_raw` property. The property value must be in a format similar to "Intel Core i9-10xxx", where the generation number is after a hyphen.
 *
 * #### `display_integer` (number)
 *
 * Round down the `display` attribute to an integer value.
 *
 * #### `storage` (object)
 *
 * If a `storage` object is not provided, one is generated using the individual storage type attributes. Each possible storage type is listed as a child property in this attribute's schema definition, and the value of that attribute is added to the object. If no storage attribute values are found, no object is created.
 *
 * Additionally, if a `storage` object does exist, the individual storage attributes are updated using the object's values. This allows both the complex `storage` attribute and the individual storage attributes to be updated by the each other, depending on which is stored/updated in the top-level product data.
 *
 * #### `storage_amount` (string)
 *
 * Adds up all storage type values for a single total storage amount value.
 *
 * #### `storage_type` (array)
 *
 * Array of all storage types the product includes, e.g. `['hdd', 'ssd', 'emmc']`.
 *
 * #### `resolution_w` and `resolution_h` (number)
 *
 * Attempts to determine the horizontal and vertical pixel dimensions of display. Parses values from `resolution` property, if in a format similar to "1920 x 1080". The dimensions divider is not important, as long as there are two numbers separated by some non-numerical character(s).
 *
 * #### `resolution_name` (string)
 *
 * Attempts to determine the current display resolution name, e.g. `hd` or `4k`. Uses an internally-defined array of display dimensions, and checks them in order from large --> small until one is found where both width and height values are not larger than product dimensions. Display dimensions used:
 *
 * - `8k` [7680, 4320]
 * - `5k` [5120, 2880]
 * - `4k` [3840, 2160]
 * - `1440p` [2560, 1440]
 * - `1080p` [1920, 1080]
 * - `720p` [1280, 720]
 *
 * *Note: attributes object is added to product in place (no value is returned).*
 *
 * ### Complex unit attributes
 *
 * For attributes with `units` defined, add their values and current units to the specified complex attributes, and set the original simple attribute value to the correct value for the current formatting unit.
 *
 * @since 3.0.0
 *
 * @param {object} product - Product object.
 *
 * @see [`App.customize`]{@link module:core/app.customize} `product` `attributes` `unrecognized`
 * @see Used by:
 * @see [**`lintProducts`**]{@link module:core/filter~lintProducts}
 *
 * @example
 * // raw product data
 * {
 *   name: "Example product",
 *   os: "Windows 10",
 *   processor: "Intel® Core™ i7 processor",
 *   hdd: "500",
 *   touchscreen: "true",
 * }
 *
 * @example
 * // expected attributes value
 * {
 *   name: "Example product",
 *   os: "Windows 10",
 *   processor: "Intel® Core™ i7 processor",
 *   hdd: "500",
 *   touchscreen: "true",
 *   attributes: {
 *     os: "windows",
 *     processor: "i7",
 *     hdd: 500,
 *     touchscreen: true,
 *   }
 * }
 */
const setProductAttributes = (product) => {
  // hold product attributes
  let attributes = {};
  let unrecognized = {};

  const cleanFloat = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return value.replace(/[^0-9.]/g, '');
  };

  const cleanString = (attrDef, attribute, value) => {
    // standardize string values to lowercase, remove reg and trade symbols
    let contentString = value.toLowerCase().replace(/™|®|&reg;|&trade;/g, '');

    // assume value is unrecognized until it is found
    let urValue = true;

    // search for attribute strings
    if (attrDef.values) {
      // check each value object
      for (let obj of attrDef.values) {
        // use either includes strings if provided, or object value as search string
        let possibleValues = obj.includes || [obj.value];
        for (let value of possibleValues) {
          if (contentString.includes(value)) {
            // set unrecognized to false
            urValue = false;
            // add brand and group for this attribute, if present
            for (let key of ['brand', 'group']) if (obj[key]) attributes[`${attribute}_${key}`] = obj[key];
            // return value
            return obj.value;
          }
        }
      }
    }

    // if unrecognized value was found, store it
    if (urValue && !attrDef.force_string) unrecognized[attribute] = contentString;

    // force content if needed
    if (attrDef.force_string) return contentString.replace(/ /g, '-');
  };

  const cleanObject = (attrDef, attribute, value) => {
    // copy value
    let obj = Object.assign({}, value);
    // if no properties are specified, return obj untouched
    if (!attrDef.properties) return obj;
    // remove any invalid properties
    for (let property of Object.keys(obj)) {
      if (!attrDef.properties.includes(property)) delete obj[property];
    }
    // return modified object, if it has any values
    return Object.keys(obj).length ? obj : false;
  };

  const cleanContent = (attrDef, attribute, value) => {
    switch (attrDef.type){
      case 'number':
        return cleanFloat(value);
      case 'string':
        return cleanString(attrDef, attribute, value);
      case 'object':
        return cleanObject(attrDef, attribute, value);
      default:
        return value;
    }
  }

  // schema attributes
  const processAttribute = (parent, attribute, attrDef, attrObj) => {
    // if no attrDef is defined, return
    if (!attrDef) return;
    // if parent does not contain attribute, return
    if (!parent[attribute] || parent[attribute] === '0') return;
    // if no attribute type is provided, throw error
    if (!attrDef.type) throw `No filter schema attribute type provided for ${attribute}`;
    // clean attribute value from parent
    const value = cleanContent(attrDef, attribute, parent[attribute]);
    // return if no value is found
    if (!value) return;
    // if type is not an object, add value to attributes object
    if (attrDef.type !== 'object') attrObj[attribute] = Util.formatValues(value, attrDef.type);
    // if type is an object, process recursively
    else {
      let childAttrObj = attrObj[attribute] = {};
      const attrSource = attrDef.properties || Object.keys(parent);
      const childDef = App.settings.filter.schema[attrDef.child]
      for (let attribute of attrSource) {
        processAttribute(value, attribute, childDef, childAttrObj);
      }
    }
  }

  // process schema attributes
  for (let attribute of Object.keys(App.settings.filter.schema)) {
    const attrDef = App.settings.filter.schema[attribute];
    processAttribute(product, attribute, attrDef, attributes);
  }

  // generated attributes

  // try to get processor generation, if not already present
  // this only works on products with processor_family_raw properties, and values with a format similar "Intel Core i9-10xxx" where the generation is after a hyphen
  if (!attributes.processor_gen && product['processor_family_raw'] && attributes.processor_brand === 'intel') {
    const gen = product['processor_family_raw'].split('-');
    // if there is post-hyphen content, add it
    if (gen.length === 2) {
      let value = parseInt(gen[1]);
      // if value is an accepted generation number, add it
      if ([10, 11, 12, 13, 14].includes(value)) attributes.processor_gen = value;
    }
  }

  // create rounded-down display integer from display value
  if (attributes.display && !attributes.display_integer) attributes.display_integer = Math.floor(attributes.display);

  // storage attributes
  // get possible storage type attribute names
  let drives = App.settings.filter.schema.storage.properties;
  // create storage object from storage drive attributes, if one does not exist
  if (!attributes.storage) {
    attributes.storage = {};
    for (let drive of drives) if (attributes[drive]) attributes.storage[drive] = attributes[drive];
    // if no storage found, add product id as storage unrecognized value
    if (!Object.keys(attributes.storage).length) unrecognized.storage = product.product_id;
  }
  // if storage object exists, set individual storage values to each storage drive value
  // this allows us to update storage values through a storage object
  if (attributes.storage) for (let d of drives) attributes[d] = attributes.storage[d] || 0;
  // add up total storage amount
  attributes.storage_amount = Object.values(attributes.storage).reduce((total, size) => total + size, 0);
  // create array of storage type attribute names
  attributes.storage_type = Object.keys(attributes.storage);

  // get resolution width & height from resolution string, if not already present
  if (!attributes.resolution_w && !attributes.resolution_h && product.resolution) {
    // identify resolution divider by stripping digits
    const divider = product.resolution.replace(/\d/g, '');
    // split into values
    const values = product.resolution.split(divider).map(px => parseFloat(px));
    // if two values remain, add them as width and height, in that order
    if (values.length === 2) {
      attributes.resolution_w = values[0];
      attributes.resolution_h = values[1];
    }
  }

  // if resolution string is not provided, but width and height are, generate resolution attribute
  if (!product.resolution && attributes.resolution_w && attributes.resolution_h) {
    attributes.resolution = `${attributes.resolution_w}-x-${attributes.resolution_h}`;
  }

  // set resolution name
  if (!attributes.resolution_name && attributes.resolution_w && attributes.resolution_h) {
    let resolutions = [
      ['8k', 7680, 4320],
      ['5k', 5120, 2880],
      ['4k', 3840, 2160],
      ['1440p', 2560, 1440],
      ['1080p', 1920, 1080],
      ['720p', 1280, 720],
    ];

    for (let size of resolutions) {
      // if either dimension is larger than the current product dimensions, skip to next smaller resolution
      if (size[1] > attributes.resolution_w || size[2] > attributes.resolution_h) continue;
      // add resolution name
      attributes.resolution_name = size[0];
      break;
    }
  }

  // create array of objects schema definitions which contain unit definitions
  let unitAttrs = Object.keys(App.settings.filter.schema).filter((name) => App.settings.filter.schema[name].units);

  // process units attributes
  for (let name of unitAttrs) {
    let def = App.settings.filter.schema[name].units;
    let currentUnit = App.settings.filter.schema[name].format_number.options[def.optionName];
    // if no complex attribute in product, create one
    if (!attributes[def.name]) {
      attributes[def.name] = {};
      // attempt to get unit from product value
      let unit = Util.parseUnit(product[name])[1];
      // if unit is not parsed, and attribute is price, check currency field
      if (!unit && name === 'price' && product.currency) unit = product.currency;
      // if unit is not parsed, get current schema unit
      if (!unit) unit = currentUnit;
      // set unit attribute value
      attributes[def.name][unit] = attributes[name];
    }
    // get default unit and value
    let defaultUnit = config.schema[name].format_number.options[def.optionName];
    let defaultValue = attributes[def.name][defaultUnit];
    // do any automatic conversions, if complex attribute contains default unit value
    if (def.convert) {
      // attempt to set a default value from default conversions
      if (!defaultValue && def.convert.default?.[currentUnit]) {
        defaultValue = def.convert.default[currentUnit](attributes[name]);
      }
      // attempt to convert default value into other units
      if (defaultValue) {
        for (let [unit, formula] of Object.entries(def.convert)) {
          // skip default conversions
          if (unit === 'default') continue;
          // if unit already exists in complex attribute, skip
          if (attributes[def.name][unit]) continue;
          // add converted value
          attributes[def.name][unit] = formula(defaultValue);
        }
      }
    }
    // set value for base attribute
    attributes[name] = attributes[def.name][currentUnit];
  }

  // if we have a recognized discrete_graphics_model, and discrete_graphics is false, set to true
  if (attributes.discrete_graphics_model && !attributes.discrete_graphics) attributes.discrete_graphics = true;

  // customize attributes
  App.customize('setProductAttributes', product, attributes, unrecognized);

  // set attributes
  Object.assign(product, {attributes, unrecognized});
};

/**
 * Some product properties need additional label formatting beyond the provided values. This function defines what properties should be labeled, and how to format those labels, through internal formatting functions (described below).
 *
 * *Note: labels are added/replaced in the product in place (nothing is returned).*
 *
 * ### Label functions
 *
 * #### `processor`
 * Add either the current processor translated label value, or, if it is not available, the product's raw processor value.
 *
 * #### `form_factor`
 * Add either the current form factor translated label value, or, if it is not available, the product's raw `form_factor` value.
 *
 * #### `price`, `display`, `weight`
 * Uses [`formatNumber`]{@link module:core/util.formatNumber} to format the value based on `format_number` options defined in schema.
 *
 * #### `storage_amount`
 * Returns a concatenated string of all available drive types for the current product, formatting the drive values using [`formatBytes`]{@link module:core/util.formatBytes}.
 *
 * #### `ssd`, `hdd`, `emmc`, `ram`
 * Format values using [`formatBytes`]{@link module:core/util.formatBytes}.
 *
 * #### `resolution`
 * Concatenate resolution width and height with an "x" separator.
 *
 * #### `ports`
 * Concatenate each available port type with its count.
 *
 * ### Custom label functions
 *
 * Label functions can be added or replaced through [project customization]{@link module:core/app.customize}. The customize function is passed this method's internal `formatFunctions` object, which contains functions to process and return a label string. Each object property is a function named after the attribute they are formatting. The labeling function is passed the current product object as a param when called.
 * ```
 *
 * @since 3.0.0
 *
 * @param {object} product - Product object
 *
 * @see [`App.customize`]{@link module:core/app.customize} `formatFunctions`
 * @see Used by:
 * @see [**`lintProducts`**]{@link module:core/filter~lintProducts}
 *
 * @example
 * // price
 * {
 *   name: "Example product",
 *   attributes: {
 *     price: 1499.5
 *   }
 *   price: "1.499,50 €" // the generated label
 * }
 *
 * @example
 * // storage_amount
 * {
 *   name: "Example product",
 *   attributes: {
 *     hdd: 1000,
 *     ssd: 256,
 *   }
 *   storage_amount: "1TB HDD + 256GB SSD" // the generated label
 * }
 *
 * @example
 * {
 *   name: "Example product",
 *   attributes: {
 *     ram: 32,
 *   }
 *   ram: "32GB", // the generated label
 * }
 *
 * @example
 * {
 *   name: "Example product",
 *   attributes: {
 *     display: 15.1,
 *   }
 *   display: '15,1"', // the generated label
 * }
 *
 * @example
 * // defining custom label functions in project customize.js
 * export const setProductLabels = (formatFunctions) => {
 *   formatFunctions.price = (product) => `${product.attributes.price} dollars`;
 * };
 */
const setProductLabels = (product) => {
  // define attributes labels, if they exist
  let attrLabels = App.settings.labels.attributes || {};
  // shortcut to filter schema
  let schema = App.settings.filter.schema;
  // define properties to format, and provide a function to process the label.
  const formatFunctions = {
    processor: (product) => (attrLabels.processor || {})[product.attributes.processor] || product.processor,
    form_factor: (product) => (attrLabels.form_factor || {})[product.attributes.form_factor] || product.form_factor,
    discrete_graphics_model: (product) => product.discrete_graphics_model,
    // format prices
    price: (product) => Util.formatNumber(product.attributes.price, schema.price.format_number.options),
    // generate storage amount labels
    storage_amount: (product) => {
      // filter storage type values to only those included in product
      let driveValues = schema.storage_type.values.filter(({value}) => product.attributes.storage_type.includes(value));
      // return formatted bytes labels
      return driveValues.map(({value: drive, label}) => `${Util.formatBytes(product.attributes[drive])} ${(attrLabels[drive] && attrLabels[drive].label) || label}`).join(' + ');
    },
    // add unit to storage and ram
    ssd: (product) => Util.formatBytes(product.attributes.ssd),
    hdd: (product) => Util.formatBytes(product.attributes.hdd),
    emmc: (product) => Util.formatBytes(product.attributes.emmc),
    ram: (product) => Util.formatBytes(product.attributes.ram),
    // add unit to display
    display: (product) => Util.formatNumber(product.attributes.display, schema.display.format_number.options),
    ratings: (product) => Util.formatNumber(product.attributes.ratings),
    resolution: (product) => `${product.attributes.resolution_w} x ${product.attributes.resolution_h}`,
    ports: (product) => {
      let ports = product.attributes.ports;
      let possiblePorts = schema.ports.properties;
      let portsLabels = attrLabels.ports || {};
      return possiblePorts.filter(name => ports[name]).map(name => `${ports[name]} ${portsLabels[name] || name}`);
    },
    weight: (product) => Util.formatNumber(product.attributes.weight, schema.weight.format_number.options),
  };

  // customize format functions
  App.customize('setProductLabels', formatFunctions);

  // create labels object
  let labels = {};

  // loop through each attribute schema and add its label (after formatting, if applicable)
  for (let [attribute, {force_label: force}] of Object.entries(App.settings.filter.schema)) {
    // skip if not found in attributes and labels are not forced
    if (!product.attributes.hasOwnProperty(attribute) && !force) continue;
    // add label
    let func = formatFunctions[attribute];
    let value = func ? func(product) : product[attribute];
    if (value) labels[attribute] = value;
  }

  // add labels object to product
  product.labels = labels;
};

/**
 * Create array of product spec objects for use in a view template. Specs to be used are defined in `display_product_specs` [filter setting]{@link module:core/config.filter}.
 *
 * Specs are formatted as objects in an array to make it possible to specify display order, and allow templating languages to loop through all specs in a predictable way. Spec objects include `name` and `value` properties, as well as any additional information needed for each spec by the project (e.g. a [rating]{@link module:core/filter~getSpecRating}).
 *
 * *Note: specs are modified/added to product in place (nothing is returned).*
 *
 * @since 3.0.0
 *
 * @param {object} product - Product object
 *
 * @see [`getSpecRating`]{@link module:core/filter~getSpecRating}
 * @see [`App.customize`]{@link module:core/app.customize} `product` `spec`
 * @see Used by:
 * @see [**`lintProducts`**]{@link module:core/filter~lintProducts}
 *
 * @example
 * // config settings
 * {
 *   display_product_specs: ['os', 'processor', 'storage_amount']
 * }
 *
 * // product data
 * let product = {
 *   name: "Example product",
 *   labels: {
 *     os: "Windows 10",
 *     processor: "Intel® Core™ i9 processor",
 *     storage_amount: "1TB HDD + 256GB SSD",
 *   },
 * };
 *
 * // set specs
 * setProductSpecs(product);
 *
 * // result
 * {
 *   name: "Example product",
 *   specs: [
 *     {
 *       name: "os",
 *       value: "Windows 10",
 *     },
 *     {
 *       name: "processor",
 *       value: "Intel® Core™ i9 processor",
 *       rating: "6",
 *     },
 *     {
 *       name: "storage_amount",
 *       value: "1TB HDD + 256GB SSD",
 *       rating: "5",
 *     }
 *   ],
 * }
 */
const setProductSpecs = (product) => {
  product.specs = [];
  // loop through display specs and create an object for each
  for (let attribute of App.settings.filter.display_product_specs) {
    // if this attribute does not exist in the product, do nothing
    if (!product.attributes[attribute]) continue;

    // skip graphics card model if unavailable
    if (attribute === 'discrete_graphics_model' && !product.attributes.discrete_graphics) continue;

    // create spec object
    let spec = {
      name: attribute,
      value: product.labels[attribute],
    };
    // add ratings to spec, if present
    let rating = getSpecRating(product, attribute);
    if (rating) spec.rating = rating;

    // customize the spec object
    App.customize('setProductSpecs', product, spec)

    // add specs object
    product.specs.push(spec);
  }
};

/**
 * Determine ratings for a given product attribute or "spec", if provided.
 *
 * For each product spec that is displayed, a rating may be provided in the attribute's schema definition. See [schema settings]{@link module:core/config.schema} for more information on configuring ratings. An overview of this process:
 *
 * - Ratings object is retrieved from the attribute's schema definition. If no ratings exist for the attribute, return `false`.
 * - Each rating consists of a rating value (the property name, e.g. `6`, `best`), and an array of conditions the product must meet in order to use that rating. Each rating condition is checked for a positive match.
 * - If a condition is met, then the current rating property name is returned. If no rating conditions are met, return `false`.
 *
 * @since 3.0.0
 *
 * @param {object} product - Product object.
 * @param {string} attribute - Name of attribute to set rating for, e.g. `processor`, `ram`.
 *
 * @return {string|boolean}
 *
 * @see [`App.attributesMatch`]{@link module:core/app.attributesMatch}
 * @see Used by:
 * @see [**`setProductSpecs`**]{@link module:core/filter~setProductSpecs}
 *
 * @example
 * // schema example for processor
 * processor: {
 *   type: 'string',
 *   ratings: [
 *     '6': [
 *       { processor: 'i7', form_factor: 'notebook' }
 *     ],
 *     '5': [
 *       { processor: 'i7', form_factor: 'desktop' }
 *     ],
 *   ],
 * }
 *
 * // products to get ratings for
 * let notebook = {
 *   name: "HP Flippybook 5-in-1",
 *   attributes: {
 *     processor: 'i7',
 *     form_factor: 'notebook',
 *   },
 * }
 *
 * let desktop = {
 *   name: "Dell Towerhouse 56000000X",
 *   attributes: {
 *     processor: 'i7',
 *     form_factor: 'desktop',
 *   },
 * }
 *
 * // get rating
 * getSpecRating(notebook, 'processor'); // expected value: 6
 * getSpecRating(desktop, 'processor'); // expected value: 5
 */
const getSpecRating = (product, attribute) => {
  // get attribute ratings
  let ratings = App.settings.filter.schema[attribute] && App.settings.filter.schema[attribute].ratings;
  // if no ratings provided for this attribute, return false
  if (!ratings) return false;
  // check ratings objects for matches
  for (let [rating, conditions] of Object.entries(ratings)) {
    for (let condition of conditions) {
      if (App.attributesMatch(condition, product.attributes)) return rating;
    }
  }

  // if no matches are found, return false
  return false;
};

/**
 * Find all products that match the current `filter.options`. If there are no matches found, an empty array will be returned.
 *
 * Filtered products are gathered by looping through `filter.products` and running a match function on each `filter.options.filter_on` property (as long as that property is part of our current filter definition in filter [`schema`]{@link module:core/config.schema}).
 *
 * Some special cases exist:
 *
 * ###### `exclude` property
 *
 * If a product has the property `exclude` set to `true`, the product will not be included in filter results unless current page is a [view all]{@link module:core/app.isViewAll} page.
 *
 * ###### Intel priority
 *
 * If on the "overwhelmed" path, a match will not be considered a success unless it contains at least one Intel product. If no Intel products exist in the results, an empty array will be returned.
 *
 * @since 3.0.0
 *
 * @return {array}
 *
 * @see [`App.attributeMatch`]{@link module:core/app.attributeMatch}
 * @see [`isIntel`]{@link module:core/filter~isIntel}
 * @see Used by:
 * @see [**`Filter.facet`**]{@link module:core/filter.facet}
 *
 * @example
 * // all products
 * [
 *   {
 *     name: "Example notebook 1",
 *     attributes: {
 *       form_factor: "notebook",
 *       processor: "i7",
 *     },
 *   },
 *   {
 *     name: "Example notebook 2",
 *     attributes: {
 *       form_factor: "notebook",
 *       processor: "i5",
 *     },
 *   },
 *   {
 *     name: "Example desktop 1",
 *     attributes: {
 *       form_factor: "desktop",
 *       processor: "i7",
 *     },
 *   },
 *   {
 *     name: "Example desktop 2",
 *     attributes: {
 *       form_factor: "desktop",
 *       processor: "i5",
 *     },
 *   },
 * ]
 *
 * // filter request
 * {
 *   filter_on: {
 *      form_factor: ["notebook", "desktop"],
 *      processor: ["i5"],
 *   }
 * }
 *
 * // result
 * [
 *   {
 *     name: "Example notebook 2",
 *     attributes: {
 *       form_factor: "notebook",
 *       processor: "i5",
 *     },
 *   },
 *   {
 *     name: "Example desktop 2",
 *     attributes: {
 *       form_factor: "desktop",
 *       processor: "i5",
 *     },
 *   },
 * ]
 */
const filterProducts = () => {
  // create array to hold products
  let filteredProducts = [];
  // loop through each product
  for (let product of data.products) {
    // skip product if exclude property is true, and not "view all" page
    if (product.exclude && !App.isViewAll()) continue;
    // set match to true, as we are looking for failure cases
    let match = true;
    // check each filter.options property for a product property match, if there are any failure cases, do not include product
    for (let [attribute, request] of Object.entries(data.request.filter_on)) {
      // get attribute path
      let attributePaths = Util.getAttributePaths(attribute, data.request.filter_on);
      // if no valid attribute paths, skip
      if (!attributePaths.length) continue;
      // check each attribute path for matching values
      match = attributePaths.reduce((m, p) => App.attributeMatch(attribute, request, Util.getObjectValue(product.attributes, p, '.'), App.settings.primary_attribute) ? true : m, false);
      // stop if we have a fail condition
      if (!match) break;
    }
    // add product if it matches all filters
    if (match) filteredProducts.push(product);
  }

  // limit non-intel results for overwhelmed path
  if (data.process !== 'facet' && filteredProducts.length && App.state.attr.app.path === 'overwhelmed') {
    // if no intel processors, empty filtered products
    let intelCount = filteredProducts.reduce((count, product) => isIntel(product) ? count + 1 : count, 0);
    if (intelCount === 0) {
      console.log('prevented non-intel product results', filteredProducts.slice());
      filteredProducts = []
    }
  }

  return filteredProducts;
}

/**
 * Score products based on proximity to the current filter request.
 *
 * Attributes making up the product score can be from a variety of sources:
 *  - User-selected options which have attributes associated with them (the main filter request)
 *  - "Passive" attributes from the `filter_options.prioritize` [filter setting]{@link module:core/config.filter}
 *  - Special cases, including:
 *      - Any attribute containing `discrete_graphics_model` will have `discrete_graphics` set to `true` automatically
 *
 * For each product, compare attributes to the current filter request attributes. A score is calculated based on values in the attribute's filter [schema]{@link module:core/config.schema} weight settings, with the following process:
 * 1. [Distance]{@link module:core/util.calcAttrDistances} is calculated based on attribute type and weighting configuration.
 * 1. If distance is 0, i.e. an exact match, the full weight score is given.
 * 1. Otherwise, attribute score is calculated as follows, using weight config parameters:
 *     1. Subtract `decay` from 10, divide by 10 to get decay proportion
 *     1. Decay proportion is then set to the power of the absolute value of the distance
 *     1. This value is multiplied by the weight value, and rounded to a single decimal
 *     1. `decay_scale` proportion is applied to adjust score value
 *     1. If distance is positive, apply `upsell_scale` proportion
 * 1. Score information is added to products in the following locations:
 *     - `score` - Final product score.
 *     - `score_max` - The highest possible score for the given request.
 *     - `score_pct` - The product's score as a percentage of the highest possible score.
 *     - `score_details` - Object containing scores for each attribute in the request. For each attribute property, an array is added which contains the following values at each index:
 *         - `[0]` attribute value score
 *         - `[1]` max attribute score
 *         - `[2]` percentage score
 *         - `[3]` calculated distance from request value
 *
 * @returns {Array}
 *
 * @see Used by:
 * @see [**`recommend`**]{@link module:core/filter.recommend}
 */
const weightedResults = () => {
  let results = [];
  // get requested user filters
  let filters = Util.cloneDeep(data.request.filter_on);
  // modify filters for special conditions
  // include discrete_graphics: true if any discrete_graphics subcategories are included
  if (Object.keys(filters).filter((k => k.includes('discrete_graphics_model'))).length) filters.discrete_graphics = true;
  // get prioritized attributes
  let prioritize = Object.entries(data.request.prioritize).reduce((a, [attribute, value]) => {
    if (value) a[attribute] = [value];
    return a;
  }, {});
  // merge filters and prioritized attributes to complete request
  let request = Util.merge(prioritize, filters);
  debug('%cWeighted filter request', 'font-weight:bold; color:white;', request);
  // get weight defaults
  let defaults = App.settings.filter.weights;
  let maxScore = 0;
  let maxScoreSet = false;
  // calculate product weights
  for (let product of data.products) {
    product.attributes.score_detail = {};
    for (let [attribute, r] of Object.entries(request)) {
      // set variables
      let def = App.settings.filter.schema[attribute];
      let p = product.attributes[attribute];
      // if no schema definition or weight value exists, skip
      if (!def?.weights?.value) continue;
      // get weight value
      let w = def.weights;
      let weight = w.value;
      // if max score has not been fully tallied, add weight value to max score
      if (!maxScoreSet) maxScore += weight;
      // initialize score and distance
      let score = 0;
      let d = 1;
      // if product attribute is defined, calculate score
      if (typeof p !== 'undefined') {
        // find distances for all supplied request values, reduce to distance closest to 0
        d = Util.calcAttrDistances(attribute, r, p, App.settings.primary_attribute);
        // if attribute is required, and distance is not 0, set score to 0 and skip
        if (def.required && d !== 0) {
          product.attributes.score_detail = {};
          break;
        }
        // calculate score
        let decay = w.decay || defaults.decay;
        let decayScale = w.decay_scale || defaults.decay_scale;
        let upsell = w.upsell_scale || defaults.upsell_scale;
        score = (d === 0) ? weight : (Math.pow((10 - decay) / 10, Math.abs(d))) * weight * decayScale;
        // add upsell modifier if distance is positive
        if (d > 0) score *= upsell;
        // round score
        score = Util.round(score, 1);
      }
      // add score to product scores object
      product.attributes.score_detail[attribute] = [score, weight, Util.round(score / weight * 100, 1), d];
    }
    // add score detail and total score to product
    product.attributes.score = Util.round(Object.values(product.attributes.score_detail).reduce((a, n) => a + n[0], 0), 1);
    // add max score and pct to product, and lock max score if not already
    product.attributes.score_max = maxScore;
    product.attributes.score_pct = Util.round(product.attributes.score / maxScore * 100, 1);
    if (!maxScoreSet) maxScoreSet = true;
    // add product to results array, if score exists
    if (product.attributes.score) results.push(product);
  }

  return results;
};

/**
 * Prepare our successfully filtered products for use in a view template. Uses `filter_options` [filter settings]{@link module:core/config.filter} and other prioritization types to order final recommendations.
 *
 * 1. Set product meta values based on current results and app settings:
 *     - `primaryCount` - if `show_more_products` [view setting]{@link module:core/config.view} is enabled, this will be the largest of either the current number of matches, or the current primary limit. Primary limit is the smallest of either the current number of matches, or the `max_primary_results` filter option. Otherwise, the `max_primary_results` is used.
 *     - `paddedCount` - if the `primaryCount` is smaller than the current request quantity, this will be the difference between the quantity and the `primaryCount` values.
 * 1. [Sort products]{@link module:core/filter~productSort} using the filter request's `sort` setting. By default, products are sorted by weighted score.
 * 1. If the current request is on a [view all]{@link module:core/app.isViewAll} page, return products, otherwise continue.
 * 1. If `lowest_price` is enabled in `filter_options`, [locate the lowest priced product]{@link module:core/filter~prioritizeLowestPrice} in exact matches and move it to the beginning of the `matches` array.
 * 1. [Prioritize exact matches]{@link module:core/filter~prioritizeExactMatches} by moving any products that exactly match requested properties to the beginning of the results array.
 * 1. [Prioritize Intel products]{@link module:core/filter~prioritizeIntel}, when applicable.
 * 1. Trim filtered products array to the requested number of products. If `show_more_products` [view setting]{@link module:core/config.view} is enabled, the number of results will be the largest of either the current `primaryCount`, or the current request quantity. Otherwise, the current request quantity will be used.
 * 1. Return products.
 *
 * @since 3.0.0
 *
 * @param {array} products - Product results.
 *
 * @param {array} matches - Exact matches to filter request. Used to [prioritize exact matches]{@link module:core/filter~prioritizeExactMatches}.
 *
 * @return {array}
 *
 * @see [`App.isViewAll`]{@link module:core/app.isViewAll}
 * @see [`productSort`]{@link module:core/filter~productSort}
 * @see [`prioritizeLowestPrice`]{@link module:core/filter~prioritizeLowestPrice}
 * @see [`prioritizeExactMatches`]{@link module:core/filter~prioritizeExactMatches}
 * @see [`prioritizeIntel`]{@link module:core/filter~prioritizeIntel}
 * @see Used by:
 * @see [**`recommend`**]{@link module:core/filter.recommend}
 */
const finalizeResults = (products, matches) => {
  debug('%cExact matches', 'font-weight:bold; color:green;', matches);
  // set product meta
  let showMore = App.settings.view.show_more_products.enabled;
  let max = data.request.max_primary_results;
  let limit = Math.min(matches.length, max);
  let quantity = data.request.quantity;
  data.resultsMeta.primaryCount = matches.length ? (showMore ? matches.length : limit) : max;
  if (data.resultsMeta.primaryCount < quantity) data.resultsMeta.paddedCount = quantity - data.resultsMeta.primaryCount;
  // if not view all, run any necessary prioritization functions
  productSort(products, ...data.request.sort);
  // if view all, stop here and return products
  if (App.isViewAll()) return products;
  prioritizeLowestPrice(matches);
  prioritizeExactMatches(products, matches);
  prioritizeIntel(products);
  // slice products to correct size
  if (data.request.quantity > 0) {
    // if view.show_more_products filter option is enabled, results size will be the largest of either the current primary count or the request quantity.
    // otherwise, it will simply be the request quantity.
    let productCount = showMore ? Math.max(data.resultsMeta.primaryCount, data.request.quantity) : data.request.quantity;
    products = products.slice(0, productCount);
  }
  // report some final states
  debug('%cResults', 'font-weight:bold; color:green;', products);
  debug('%cResults weighted scores', 'font-weight:bold; color:white;', Object.values(products).map((p) => Object.assign({}, p.attributes.score_detail, {total: p.attributes.score})));

  return products;
}

/**
 * Sort array of products by provided attributes.
 *
 * Each attribute is used to sort products in the order provided. I.e., the attribute that will create the largest product "buckets" should be first, and each subsequent attribute will sort within the previous "bucket".
 *
 * Sort order depends on several factors:
 *
 * - If an `order` exists in the attribute's [schema]{@link module:core/config.schema} definition (either `ASC` or `DESC`), that order will be used.
 * - If predefined attribute value order exists in its [schema]{@link module:core/config.schema} `values` property, the value index will be used in the compare function.
 * - If no predefined order exists, order is set depending on type:
 *     - `string`, `boolean`: `ASC`
 *     - `number`: `DESC`
 * - Any undefined values are always placed last in sort order.
 *
 * @since 3.0.0
 *
 * @param {array} products - Array of products to sort.
 * @param {...string} attributes - Attribute(s) to sort products by.
 *
 * Note that provided attributes must have a [schema]{@link module:core/config.schema} definition or they cannot be used to sort, since they will not be present in product attributes.
 *
 * @see Used by:
 * @see [**`finalizeResults`**]{@link module:core/filter~finalizeResults}
 */
const productSort = (products, ...attributes) => {
  products.sort((a, b) => {
    for (let attribute of attributes) {
      // initialize compare values
      let va, vb;
      // get attribute definition
      let def = App.settings.filter.schema[attribute];
      // if no definition, skip attribute
      if (!def) continue;
      // if we have values/order defined, use order index as compare value
      const values = def.values;
      if (values) {
        // get indices of value objects. this will act as their priority
        const ia = Util.indexOfObjectByProperty('value', a.attributes[attribute], values);
        const ib = Util.indexOfObjectByProperty('value', b.attributes[attribute], values);
        // if we found the object (index not -1), set value to index
        // if object was not found, set to false
        va = ia === -1 ? false : ia;
        vb = ib === -1 ? false : ib;
      }

      // compare by attribute value directly
      // if attribute is undefined, set to false
      else {
        va = typeof a.attributes[attribute] === 'undefined' ? false : a.attributes[attribute];
        vb = typeof b.attributes[attribute] === 'undefined' ? false : b.attributes[attribute];
      }

      // get sort order
      // number types are sorted descending by default
      // all other types are sorted ascending by default
      let defaultOrder = def.values ? 'ASC' : def.type === 'number' ? 'DESC' : 'ASC';
      let order = def.order ?? defaultOrder;
      // if a is defined and b is not, a first
      if (va !== false && vb === false) return -1;
      // if b is defined and a is not, b first
      if (va === false && vb !== false) return 1;
      // sort numbers in descending order
      if (order === 'ASC') {
        // if a is lower than b, a first
        if (va < vb) return -1;
        // if b is lower than a, b first
        if (va > vb) return 1;
      }
      // sort descending
      else {
        // if a is lower than b, b first
        if (va < vb) return 1;
        // if b is lower than a, a first
        if (va > vb) return -1;
      }
    }
  });
};

/**
 * For any products that are an exact match to the current filter request, move them to the beginning of the product array. Any existing weighted order will be preserved within the prioritized products.
 *
 * This functionality can be disabled with the `filter_options.prevent_exact_matches` [filter setting]{@link module:core/config.filter}.
 *
 * Product order will be modified **in place**.
 *
 * @param {array} products - Products to prioritize.
 *
 * @param {array} matches - Exact product matches.
 *
 * @see [`filterProducts`]{@link module:core/filter~filterProducts}
 */
const prioritizeExactMatches = (products, matches) => {
  // if exact matches should not be prioritized, do nothing
  if (data.request.prevent_exact_matches) return;
  let insertionIndex = 0;
  for (let i = 0; i < products.length; i++) {
    let id = products[i].product_id;
    let matchIndex = Util.indexOfObjectByProperty('product_id', id, matches);
    if (matchIndex > -1) {
      products.splice(insertionIndex++, 0, ...products.splice(i, 1));
    }
  }
};

/**
 * If products have prices, finds the lowest priced product and moves it into the first result position. An overview of the process:
 *
 * 1. Lowest price is retrieved from products using [`lowestPrice`]{@link module:core/filter.lowestPrice}. If no price is found, the function stops here and no rearranging occurs.
 * 2. If lowest price is found, reduce products array to the index of the product that contains the lowest price. If multiple products match the lowest price, the first one found will be used.
 * 3. Lowest-priced product is spliced from the array and added to the beginning.
 *
 * *This optional feature is enabled with the `lowest_price` filter option. More information on usage can be found in `filter_options` in [filter settings]{@link module:core/config.filter}.*
 *
 * @since 3.0.0
 *
 * @param {array} products - Array of product objects.
 *
 * @see [`lowestPrice`]{@link module:core/filter.lowestPrice}
 * @see Used by:
 * @see [**`finalizeResults`**]{@link module:core/filter~finalizeResults}
 */
const prioritizeLowestPrice = (products) => {
  // if lowest price product is not enabled, do nothing
  if (!data.request.lowest_price) return;
  // find lowest prices
  let lowest = lowestPrice(products);
  // if there are no prices found, do nothing
  if (!lowest) return;
  for (let i = 0; i < products.length; i++) {
    // if product is not lowest price, skip it
    if (products[i].attributes.price !== lowest) continue;
    // splice out lowest priced product and add to beginning of array
    products.unshift(products.splice(i, 1)[0]);
    return;
  }
};

/**
 * Order provided products with a priority on Intel processors. Only enabled on app paths which have the `app` attribute value `overwhelmed` set.
 *
 * There are two main parts to this prioritization process:
 *
 * 1. The first product in the filtered products array MUST be an Intel product. If it is not, an Intel product is located and moved to the first array position. Note that when this setting is enabled, a recommendation will ALWAYS include an Intel product, because a result that does not contain an Intel processor will be considered a match failure during [`filterProducts`]{@link module:core/filter~filterProducts}.
 * 2. There must not be more than one non-Intel product in the recommendation. If a non-Intel product is used within the recommendation limit, each additional non-Intel products within the recommendation limit will be removed, until an Intel product is found to fill the spot (or there are no more products remaining). For each product removed, the `primaryCount` results meta value is decreased, and `paddedCount` increased (if applicable).
 *
 * @since 3.0.0
 *
 * @param {array} products - Array of product objects.
 *
 * @see [`isIntel`]{@link module:core/filter~isIntel}
 * @see Used by:
 * @see [**`finalizeResults`**]{@link module:core/filter~finalizeResults}
 */
const prioritizeIntel = (products) => {
  // if not on overwhelmed path, do nothing
  if (App.state.attr.app.path !== 'overwhelmed') return;
  debug('prioritizing intel processors');
  // if first product is not intel, find intel product and move it to front of the line
  // only applies to overwhelmed path
  if (!isIntel(products[0])) {
    debug('first product is non-intel. moving intel product to start');
    // reduce products to the first found intel product index
    let intelProductIndex = products.reduce((index, product, i) => index === -1 && isIntel(product) ? i : index, -1);
    // splice out intel product and add to beginning of array
    products.unshift(products.splice(intelProductIndex, 1)[0]);
  }

  // limit number of non-intel products within quantity
  let nonIntelLimit = 1;
  let validProductsFound = 0;
  for (let i = 0; validProductsFound < data.request.quantity && i < products.length; i++) {
    // check if product at current index is non-intel
    if (!isIntel(products[i])) {
      // if our non-intel product limit hasn't been reached, product is okay to keep
      if (data.resultsMeta.nonIntelCount < nonIntelLimit) {
        debug('non-intel product found but limit not reached. including product:', products[i]);
        validProductsFound++;
      }
      // otherwise, remove it and de-increment loop
      else {
        debug('non-intel product limit reached. removing product:', products[i]);
        // update results meta if current index is less than primary results
        if (i < data.resultsMeta.primaryCount) {
          // de-increment primary count
          data.resultsMeta.primaryCount--;
          // if primary count is less than quantity, increment padded count
          if (data.resultsMeta.primaryCount < data.request.quantity) data.resultsMeta.paddedCount++;
          console.log(data.resultsMeta);
        }
        products.splice(i--, 1);
      }
      // iterate non-intel count
      data.resultsMeta.nonIntelCount++;
    }
    // if intel, iterate valid products
    else validProductsFound++;
  }
};

/**
 * Determine if a given product is using Intel processors. Checks a product's `attributes.processor` against processor brand values in [schema]{@link module:core/config.schema}.
 *
 * If no brand is provided for the processor, or brand is not `intel`, returns `false`.
 *
 * @since 3.0.0
 *
 * @param {object} product - Product object to test for presence of Intel processor.
 *
 * @return {boolean}
 *
 * @see Used by:
 * @see [**`filterProducts`**]{@link module:core/filter~filterProducts}
 * @see [**`prioritizeIntel`**]{@link module:core/filter~prioritizeIntel}
 */
const isIntel = (product) => product.attributes.processor_brand === 'intel';

const debug = (...content) => {
  if (App.settings.filter.debug) {
    console.log(...content);
  }
};
